import { useState, useEffect, useMemo } from 'react'
import TrainingViewer from './TrainingViewer'
import { CAMS_PREP_IDS, CAMS_PREP_MODULES, CAMS_PREP_HANDS_ON, CAMS_PREP_COURSE, CAMS_PREP_CASE_STUDIES } from '../data/camsPrepCourse'
import { downloadTrainingCertificate } from '../utils/certificateGenerator'
import CourseRating from './CourseRating'
import './CAMSCourse.css'

// CrossReg Nexus CAMS exam preparation: eight study modules (10-question knowledge check and five
// CrossRegGuard hands-on labs each), then a final practice exam drawn from every module's questions.
// Module completions and the certificate are recorded server-side (/api/training/*).
const PREP_MODULES = CAMS_PREP_IDS
const EXAM_PASS_MARK = CAMS_PREP_COURSE.examPassMark
const COURSE_TITLE = CAMS_PREP_COURSE.title

function shuffle(list) {
  const a = [...list]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function moduleInfo(id) {
  const m = CAMS_PREP_MODULES[id] || {}
  const slides = m.slides || []
  const quiz = slides.find(s => s.isQuiz)
  return {
    id,
    number: m.number,
    domain: m.domain,
    title: m.title || id,
    description: m.description || '',
    slideCount: slides.filter(s => !s.isQuiz).length,
    questions: quiz?.questions || [],
    labCount: CAMS_PREP_HANDS_ON[id]?.tasks?.length || 0,
  }
}

export default function CAMSCourse({ userId, userEmail }) {
  const modules = useMemo(() => PREP_MODULES.map(moduleInfo), [])
  const [assignments, setAssignments] = useState({}) // module_id -> assignment row
  const [certificate, setCertificate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [viewing, setViewing] = useState(null)
  const [exam, setExam] = useState(null) // { questions, answers, submitted, score }
  const [savingCert, setSavingCert] = useState(false)
  const [certError, setCertError] = useState(null)
  // A passed exam is remembered so a certificate that failed to issue can be retried without retaking the exam
  const passKey = `cams_exam_passed_${userId}`
  const readPassed = () => { try { const v = JSON.parse(localStorage.getItem(passKey) || 'null'); return v && v.score >= EXAM_PASS_MARK ? v : null } catch { return null } }
  const [passedExam, setPassedExam] = useState(readPassed)

  const load = async () => {
    try {
      const [aRes, cRes] = await Promise.all([
        fetch(`/api/training/member-trainings?userId=${encodeURIComponent(userId)}&track=certification`, { credentials: 'include' }),
        fetch(`/api/training/certificates?userId=${encodeURIComponent(userId)}`, { credentials: 'include' }),
      ])
      if (!aRes.ok) throw new Error('Could not load your progress')
      const rows = await aRes.json()
      const map = {}
      for (const r of rows) {
        if (!PREP_MODULES.includes(r.module_id)) continue
        // Prefer a completed row if the module is assigned more than once
        if (!map[r.module_id] || r.status === 'completed') map[r.module_id] = r
      }
      setAssignments(map)
      if (cRes.ok) {
        const certs = await cRes.json()
        setCertificate(certs.find(c => c.training_id === 'cams-prep') || null)
      }
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { if (userId) load() }, [userId])

  const completedCount = PREP_MODULES.filter(id => assignments[id]?.status === 'completed').length
  const allDone = completedCount === PREP_MODULES.length
  const overall = Math.round((completedCount / PREP_MODULES.length) * 100)

  const saveStatus = async (moduleId, status, extra = {}) => {
    const res = await fetch('/api/training/update-training-status', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ moduleId, status, ...extra }),
    })
    if (!res.ok) {
      const e = await res.json().catch(() => ({}))
      throw new Error(e.error || 'Could not save progress')
    }
  }

  const openModule = async (m) => {
    try {
      if (!assignments[m.id] || assignments[m.id].status === 'pending') {
        await saveStatus(m.id, 'in-progress')
        setAssignments(prev => ({ ...prev, [m.id]: { ...(prev[m.id] || { module_id: m.id }), status: 'in-progress' } }))
      }
      setViewing({ module_id: m.id, module_title: m.title, module_name: m.title })
    } catch (err) {
      alert(err.message)
    }
  }

  const handleModuleComplete = async (moduleId, result) => {
    try {
      await saveStatus(moduleId, 'completed', { quizScore: result?.quizScore })
      if (result?.certificateName) localStorage.setItem(`training_name_${userId}`, result.certificateName)
      await fetch('/api/training/certification-progress', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          certificationId: 'cams-prep',
          status: 'in-progress',
          progressPercentage: Math.round(((completedCount + (assignments[moduleId]?.status === 'completed' ? 0 : 1)) / PREP_MODULES.length) * 100),
        }),
      }).catch(() => null)
    } catch (err) {
      alert(err.message)
    }
    load()
  }

  const startExam = () => {
    const pool = [
      ...modules.flatMap(m => m.questions.map(q => ({ ...q, module: m.title }))),
      ...CAMS_PREP_CASE_STUDIES.map(q => ({ ...q, module: 'Case study' })),
    ]
    setExam({ questions: shuffle(pool), answers: {}, submitted: false, score: null })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const issueCertificate = async (score) => {
    setSavingCert(true)
    setCertError(null)
    try {
      const name = localStorage.getItem(`training_name_${userId}`) || ''
      const res = await fetch('/api/training/certificate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score, certificateName: name || undefined }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data?.code) throw new Error(data.error || 'Could not issue the certificate')
      setCertificate(data)
      try { localStorage.removeItem(passKey) } catch { /* ignore */ }
      setPassedExam(null)
    } catch (err) {
      setCertError(`${err.message}. Your passing score is saved — use "Issue my certificate" to try again.`)
    } finally {
      setSavingCert(false)
    }
  }

  const submitExam = async () => {
    const correct = exam.questions.filter((q, i) => exam.answers[i] === q.correctAnswer).length
    const score = Math.round((correct / exam.questions.length) * 100)
    setExam({ ...exam, submitted: true, score, correct })
    if (score < EXAM_PASS_MARK) return
    const passed = { score, at: new Date().toISOString() }
    try { localStorage.setItem(passKey, JSON.stringify(passed)) } catch { /* ignore */ }
    setPassedExam(passed)
    await issueCertificate(score)
  }

  const downloadCertificate = () => {
    if (!certificate) return
    downloadTrainingCertificate(
      COURSE_TITLE,
      certificate.recipient_name || userEmail,
      certificate.issued_at,
      'CrossReg Nexus',
      certificate.code,
      { kind: 'the certification preparation course, including all eight modules, hands-on labs and the final practice exam',
        score: certificate.score ?? undefined, expiresAt: certificate.expires_at || undefined }
    )
  }

  if (loading) return <div className="cams-course-container"><p>Loading course…</p></div>

  // ── Final exam ──
  if (exam) {
    const answered = Object.keys(exam.answers).length
    return (
      <div className="cams-course-container">
        <div className="quiz-container">
          <button className="back-btn" onClick={() => setExam(null)}>← Back to modules</button>
          <h2>Final practice exam</h2>
          <p>{exam.questions.length} questions: every module's knowledge-check questions plus {CAMS_PREP_CASE_STUDIES.length} case studies · pass mark {EXAM_PASS_MARK}%</p>

          {exam.submitted ? (
            <div className="quiz-results">
              <div className="score-display">
                <div className="score-number">{exam.score}%</div>
                <p className={exam.score >= EXAM_PASS_MARK ? 'passed' : 'failed'}>
                  {exam.score >= EXAM_PASS_MARK ? '✓ Passed' : '✗ Not yet passed'} — {exam.correct} of {exam.questions.length} correct
                </p>
              </div>
              {exam.score >= EXAM_PASS_MARK ? (
                <>
                  <p className="score-message">{savingCert ? 'Issuing your certificate…' : certificate ? 'Your certificate has been issued.' : ''}</p>
                  {certError && <p className="error-message" role="alert">{certError}</p>}
                  {certificate && <button className="download-btn" onClick={downloadCertificate}>📄 Download certificate</button>}
                  {certificate && <CourseRating moduleId="cams-prep" courseTitle={COURSE_TITLE} />}
                  {!certificate && !savingCert && certError && <button className="download-btn" onClick={() => issueCertificate(exam.score)}>🎖️ Issue my certificate</button>}
                </>
              ) : (
                <>
                  <p className="score-message">Review the modules covering the questions you missed, then try again.</p>
                  <button className="complete-btn" onClick={startExam}>Retake exam</button>
                </>
              )}
              <div className="quiz-questions" style={{ marginTop: 24 }}>
                {exam.questions.map((q, i) => {
                  const ok = exam.answers[i] === q.correctAnswer
                  return (
                    <div key={i} className="quiz-question" style={{ borderLeft: `4px solid ${ok ? '#10b981' : '#ef4444'}`, paddingLeft: 12 }}>
                      <p><strong>{i + 1}.</strong> {q.question} <small style={{ color: '#6b7280' }}>({q.module})</small></p>
                      {!ok && <p style={{ color: '#065f46' }}>Correct answer: {q.options[q.correctAnswer]}</p>}
                      {q.explanation && <p style={{ color: '#4b5563', fontSize: 13 }}>{q.explanation}</p>}
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <>
              <div className="quiz-questions">
                {exam.questions.map((q, i) => (
                  <div key={i} className="quiz-question">
                    <p><strong>Question {i + 1}:</strong> {q.question}</p>
                    <div className="options">
                      {q.options.map((opt, oi) => (
                        <label key={oi} className="option">
                          <input
                            type="radio"
                            name={`exam-q${i}`}
                            checked={exam.answers[i] === oi}
                            onChange={() => setExam({ ...exam, answers: { ...exam.answers, [i]: oi } })}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button className="submit-btn" onClick={submitExam} disabled={answered < exam.questions.length}>
                Submit exam ({answered}/{exam.questions.length} answered)
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  // ── Module list ──
  return (
    <div className="cams-course-container">
      <div className="cams-header">
        <div className="cams-header-content">
          <h1>{COURSE_TITLE}</h1>
          <p className="subtitle">By CrossReg Nexus · {PREP_MODULES.length} study modules · 10-question knowledge check and 5 CrossRegGuard labs per module · final practice exam</p>
          <div className="progress-info">
            <div className="progress-bar"><div className="progress-fill" style={{ width: `${overall}%` }} /></div>
            <p>{completedCount} of {PREP_MODULES.length} modules completed ({overall}%)</p>
          </div>
        </div>
      </div>

      {error && <div className="error-message" role="alert">{error}</div>}

      <div className="modules-grid">
        {modules.map((m, idx) => {
          const a = assignments[m.id]
          const status = a?.status || 'not-started'
          return (
            <div key={m.id} className={`module-card ${status === 'completed' ? 'completed' : ''}`}>
              <div className="module-header">
                <span className="module-number">{idx + 1}</span>
                <span className={`status ${status === 'completed' ? 'complete' : ''}`}>
                  {status === 'completed' ? `✓ Complete${a.quiz_score != null ? ` · ${a.quiz_score}%` : ''}` : status === 'in-progress' ? 'In progress' : 'Not started'}
                </span>
              </div>
              <p style={{ fontSize: 11, color: '#6b7280', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.4 }}>{m.domain}</p>
              <h3>{m.title}</h3>
              <p className="objective">{m.description}</p>
              <p className="duration">📑 {m.slideCount} slides · {m.questions.length} questions · 🛠 {m.labCount} CrossRegGuard labs</p>
              <button className="start-btn" onClick={() => openModule(m)}>
                {status === 'completed' ? '📖 Review' : status === 'in-progress' ? '▶️ Continue' : '▶️ Start'}
              </button>
            </div>
          )
        })}

        {allDone && !certificate && (
          <div className="module-card final-exam-card">
            <div className="module-header">
              <span className="module-number">✓</span>
              <span className="status complete">Ready</span>
            </div>
            <h3>🎖️ Final practice exam</h3>
            <p className="objective">{modules.reduce((n, m) => n + m.questions.length, 0) + CAMS_PREP_CASE_STUDIES.length} questions: all {modules.reduce((n, m) => n + m.questions.length, 0)} knowledge-check questions from the {PREP_MODULES.length} modules plus {CAMS_PREP_CASE_STUDIES.length} case studies, in random order. Pass mark {EXAM_PASS_MARK}%.</p>
            {passedExam ? (
              <>
                <p style={{ color: '#065f46', fontWeight: 600 }}>✓ You passed the final exam with {passedExam.score}%.</p>
                {certError && <p className="error-message" role="alert">{certError}</p>}
                <button className="start-btn exam-btn" disabled={savingCert} onClick={() => issueCertificate(passedExam.score)}>
                  {savingCert ? 'Issuing…' : '🎖️ Issue my certificate'}
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize: 13, color: '#4b5563' }}>Pass the final exam to receive your signed course certificate.</p>
                <button className="start-btn exam-btn" onClick={startExam}>📝 Take final exam</button>
              </>
            )}
          </div>
        )}

        {certificate && (
          <div className="module-card certificate-card">
            <div className="module-header">
              <span className="module-number">🎉</span>
              <span className="status complete">Course complete</span>
            </div>
            <h3>{COURSE_TITLE}</h3>
            {certificate.score != null && <p className="exam-score">Final exam: {certificate.score}%</p>}
            <p className="cert-code">Certificate: {certificate.code}</p>
            <div className="certificate-actions">
              <button className="download-btn" onClick={downloadCertificate}>📄 Download certificate</button>
            </div>
            <CourseRating moduleId="cams-prep" courseTitle={COURSE_TITLE} />
          </div>
        )}
      </div>

      <p style={{ color: '#6b7280', fontSize: 12, marginTop: 24 }}>
        {CAMS_PREP_COURSE.disclaimer}
      </p>

      {viewing && (
        <TrainingViewer
          training={viewing}
          userId={userId}
          defaultCertificateName={localStorage.getItem(`training_name_${userId}`) || ''}
          onClose={() => { setViewing(null); load() }}
          onComplete={(result) => handleModuleComplete(viewing.module_id, result)}
        />
      )}
    </div>
  )
}
